import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { publicInstance as axiosInstance } from '../axiosConfig';
import { Project, ProjectSettings } from './projectSlice';
import Cookies from 'js-cookie';
import  _ from 'lodash';




interface PublicProjectState {
  currentProject: Project | null;
  formPages: any[];
  projectSettings: ProjectSettings | null;
  isLoading: boolean;
  error: string | null;
  generatedQuestion: null;
  generatedQuestionOptions: null | any[];
  answerType: null | string;
  answerTypeValue: {
    key: string;
    value: string;
  } | null;
  options: null | any[];
  correctIndex: null | number;
  conversationUuid?: string | undefined;
  callToActionUrl?: string | undefined;
  callToActionTarget?: string | undefined;
  isRequired: boolean;
  projectMedia: [];
  project_type: undefined | null | string;
  isConversationCompleted: boolean;
  generatingFormPageQuestionIsLoading: boolean;
  form_password: string | null;
  form_password_enabled: boolean;
  isPasswordVerified: boolean;
  verifyingPassword: boolean;
  conversation: any;
  showCountBar: boolean;
  skipWelcomeScreen: boolean;
  enableRestoreChat: boolean;
  submissionLimit: number | null;
  submissionCount: number;
  isFormClosed: boolean;
  showQuestionClarity: boolean;
  showOtherRespondents: boolean;
  otherRespondentsData: any[] | null;
  userSource: string | null;
  thankYouEmailFormat: 'html_css_enabled_style' | 'plain_text';
  isProjectClosed: boolean;
  isSubmissionLimitReached: boolean;
  closureReason?: string;
  show_user_details_filled_end_of_screen: boolean;
  allow_edit_details_end_of_screen: boolean;
  clarityExplanation: string | null;
  userDetails: Record<string, any> | null;
  respondentInsights?: {
    completion_rate: string;
    survival_rate: string;
    time_insight: string;
    progress_relative: string;
    real_time_feeling: string;
  } | null;
  projectLogicTemplate: any | null;
  conditionalLogics: any[] | null;
}

const initialState: PublicProjectState = {
  currentProject: null,
  formPages: [],
  projectSettings: null,
  isLoading: false,
  error: null,
  generatedQuestion: null,
  generatedQuestionOptions: null,
  answerType: null,
  answerTypeValue: null,
  options: null,
  correctIndex: null,
  conversationUuid: undefined,
  callToActionUrl: undefined,
  callToActionTarget: undefined,
  isRequired: false,
  projectMedia: [],
  project_type: null,
  isConversationCompleted: false,
  generatingFormPageQuestionIsLoading: false,
  form_password: null,
  form_password_enabled: false,
  isPasswordVerified: false,
  verifyingPassword: false,
  conversation: null,
  showCountBar: true,
  skipWelcomeScreen: false,
  enableRestoreChat: true,
  submissionLimit: null,
  submissionCount: 0,
  isFormClosed: false,
  showQuestionClarity: false,
  showOtherRespondents: false,
  otherRespondentsData: null,
  userSource: null,
  thankYouEmailFormat: 'html_css_enabled_style',
  isProjectClosed: false,
  isSubmissionLimitReached: false,
  closureReason: undefined,
  show_user_details_filled_end_of_screen: false,
  allow_edit_details_end_of_screen: false,
  clarityExplanation: null,
  userDetails: null,
  respondentInsights: null,
  projectLogicTemplate: null,
  conditionalLogics: null,
};


const CONVERSATION_ID_COOKIE_PUBLIC = 'form_conversation_id';





export const saveConversationIdToCookie = (conversationId: string, projectId: number | string | undefined) => {
  // Create a unique cookie key based on the form ID
  const cookieKey = `${CONVERSATION_ID_COOKIE_PUBLIC}_${projectId}`;
  Cookies.set(cookieKey, conversationId, { expires: 1 });
};

export const removeConversationIdCookie = (projectId: string | number | undefined) => {
  // Remove the specific cookie for this form
  const cookieKey = `${CONVERSATION_ID_COOKIE_PUBLIC}_${projectId}`;
  Cookies.remove(cookieKey);
};

export const getConversationIdFromCookie = (projectId: string | number | undefined) => {
  // Get the conversation ID for a specific form
  const cookieKey = `${CONVERSATION_ID_COOKIE_PUBLIC}_${projectId}`;
  return Cookies.get(cookieKey);
};

// Alternative solution if you need conversationUuid
export const fetchPublicProject = createAsyncThunk(
  'publicProject/fetchPublicProject',
  async ({ shareableLink, conversationUuid }: { shareableLink: string; conversationUuid?: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/projects/public/${shareableLink}/${conversationUuid || ''}`);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        if (error.response.status === 410 && error.response.data.message === 'PROJECT_CLOSED') {
          return rejectWithValue({
            status: 410,
            code: 'PROJECT_CLOSED',
            message: error.response.data.message || 'This form is closed'
          });
        }
        if (error.response.status === 409 && error.response.data.message === 'SUBMISSION_LIMIT_REACHED') {
          return rejectWithValue({
            status: 409,
            code: 'SUBMISSION_LIMIT_REACHED',
            message: error.response.data.message || 'Submission limit reached'
          });
        }
      }
      // For other errors
      return rejectWithValue({
        status: error.response?.status || 500,
        code: error.response?.data?.code || 'ERROR',
        message: error.response?.data?.message || error.message || 'An error occurred'
      });
    }
  }
);

export const generatePublicQuestion = createAsyncThunk(
  'publicProject/generatePublicQuestion',
  async (
    {
      shareableLink,
      formPageId,
      userIdentifier,
      conversationUuid,
      answer,
      file,
    }: {
      shareableLink: string;
      formPageId?: number;
      userIdentifier: string;
      conversationUuid?: string;
      answer?: any;
      file?: File | null;
    },
    { rejectWithValue }
  ) => {
    try {
      let response;
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('conversation_uuid', conversationUuid || '');
        formData.append('user_identifier', userIdentifier);
        if (answer) {
          formData.append('answer', answer.toString());
        }
        response = await axiosInstance.post(
          `/projects/public/${shareableLink}/form-pages/${formPageId}/generate-question`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
      } else {
        response = await axiosInstance.post(
          `/projects/public/${shareableLink}/form-pages/${formPageId}/generate-question`,
          {
            conversation_uuid: conversationUuid,
            user_identifier: userIdentifier,
            answer,
          }
        );
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const verifyFormPassword = createAsyncThunk(
  'publicProject/verifyFormPassword',
  async (
    { shareableLink, password }: { shareableLink: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      console.log('axiosInstance', axiosInstance.defaults.baseURL);
      const response = await axiosInstance.post(
        `/projects/public/${shareableLink}/verify-password`,
        { password }
      ); 
      console.log('hello world');
      console.log('response', response); 
      return {
        status: response.status,
        data: response.data,
      };
    } catch (error: any) {

      return {
        status: error.response?.status,
        data: error.response?.data,
      };
    }
  }
);

const publicProjectSlice = createSlice({
  name: 'publicProject',
  initialState,
  reducers: {
    resetPublicProjectState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicProject.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPublicProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProject = action.payload.project;
        state.formPages = action.payload.project.form_pages;
        state.projectSettings = action.payload.project.settings;
        state.form_password = action.payload.project.settings?.form_password || null;
        state.form_password_enabled =
          !!action.payload.project.settings?.form_password_enabled;
        state.conversationUuid = action.payload.conversation?.conversation_uuid;
        
        // Set show_user_details_filled_end_of_screen from projectSettings
        state.show_user_details_filled_end_of_screen = 
          !!action.payload.project.settings?.show_user_details_filled_end_of_screen;
        
        // Set allow_edit_details_end_of_screen from projectSettings
        state.allow_edit_details_end_of_screen = 
          !!action.payload.project.settings?.allow_edit_details_end_of_screen;
        
        // Set userDetails if available in the response
        if (action.payload.user_details) {
          state.userDetails = action.payload.user_details;
        }
        
        // Set respondentInsights if available and show_other_respondents is enabled
        if (action.payload.respondent_insights && 
            action.payload.project.settings?.show_other_respondents) {
          state.respondentInsights = action.payload.respondent_insights;
        }
        
        const conversation = action.payload.conversation;
        if(conversation?.entries.length > 0) {
          state.conversation = conversation;
          let entries = conversation.entries;
          let lastEntry = entries[entries.length - 1];
          state.generatingFormPageQuestionIsLoading = false;
          state.generatedQuestion = lastEntry.question; 
          state.answerType = lastEntry.answer_type;
          state.options = lastEntry.options;
          state.generatedQuestionOptions = lastEntry.options;
          state.conversationUuid = action.payload.conversation.uuid; 
          state.isRequired = action.payload.conversation.is_required;
          state.callToActionUrl = action.payload.conversation.call_to_action_url;
          state.callToActionTarget = action.payload.conversation.call_to_action_target;
          state.isConversationCompleted =
          action.payload.conversation.is_conversation_completed;
          state.clarityExplanation = lastEntry.clarity_explanation || null;
          const project = action.payload.project;
           const formPages = action.payload.project.form_pages;
           // adaptive forms on page init load to help user continue
           if(project.project_type === 'adaptive_forms') {
            const welcomePage = formPages.find(
              (page: any) => page.page_type === 'welcome'
            );
            const endPage = formPages.find(
              (page: any) => page.page_type === 'end'
            );
            // add end to end of entries array
            const entriesWithEndPosition = [...entries, endPage];
            // add welcome to start of entries array
            const entriesWithStartAndEndPosition = [welcomePage, ...entriesWithEndPosition];
            entriesWithStartAndEndPosition.forEach((page: any, index: number) => {
              page.correct_index = index;
              if(_.isEmpty(page.position)){
                page.position = index;
                page.form_page_files = welcomePage?.form_page_files || [];
                page.project_id = welcomePage?.project_id || null;
                page.organization_id = welcomePage?.organization_id || null;
              }
              if(_.isEmpty(page?.page_type)){
                page.page_type = 'prompt';
              }
            });
            // get the second to last entry and set the correct index to the second to last entry
            // since that is not the endpage
            console.log('entriesWithStartAndEndPosition', entriesWithStartAndEndPosition);
            const lastEntryAfterOrganizingFormPages = entriesWithStartAndEndPosition[entriesWithStartAndEndPosition.length - 2];
            console.log('lastEntryAfterOrganizingFormPages', lastEntryAfterOrganizingFormPages);
            state.correctIndex = lastEntryAfterOrganizingFormPages.correct_index;
            state.formPages = entriesWithStartAndEndPosition;
           }
            
        }
        
        // Set the new properties from project settings
        state.showCountBar = action.payload.project.settings?.show_count_bar ?? true;
        state.skipWelcomeScreen = action.payload.project.settings?.skip_welcome_screen ?? false;
        state.enableRestoreChat = action.payload.project.settings?.enable_restore_chat ?? true;
        
        if (action.payload.project.settings?.enable_submission_limit) {
          state.submissionLimit = action.payload.project.settings.submission_limit;
          state.isFormClosed = state.submissionLimit !== null && 
                               action.payload.project.submission_count >= state.submissionLimit && 
                               action.payload.project.settings.close_after_submission_limit;
        } else {
          state.submissionLimit = null;
          state.isFormClosed = false;
        }
        
        state.submissionCount = action.payload.project.submission_count || 0;
        state.showQuestionClarity = action.payload.project.settings?.show_question_clarity ?? false;
        state.showOtherRespondents = action.payload.project.settings?.show_other_respondents ?? false;
        state.otherRespondentsData = action.payload.project.other_respondents_data || null;
        
        // Set user source if tracking is enabled
        if (action.payload.project.settings?.track_user_source) {
          state.userSource = action.payload?.user_source || null;
        }
        
        // Add handling for the new property
        state.thankYouEmailFormat = action.payload.project.settings?.thank_you_email_format ?? 'html_css_enabled_style';
        
        // Add the new properties
        state.projectLogicTemplate = action.payload.project_logic_template || null;
        state.conditionalLogics = action.payload.conditional_logics || null;
      })
      .addCase(fetchPublicProject.rejected, (state, action: any) => {
        state.isLoading = false;
        
        if (action.payload) {
          if (action.payload.code === 'PROJECT_CLOSED') {
            state.isProjectClosed = true;
            state.closureReason = 'closed';
            state.error = action.payload.message;
          } else if (action.payload.code === 'SUBMISSION_LIMIT_REACHED') {
            state.isProjectClosed = true;
            state.isSubmissionLimitReached = true;
            state.closureReason = 'submission-limit';
            state.error = action.payload.message;
          } else {
            state.error = action.payload.message || 'An error occurred';
          }
        } else {
          state.error = 'An unexpected error occurred';
        }
      })
      .addCase(generatePublicQuestion.pending, (state) => {
        state.generatingFormPageQuestionIsLoading = true;
        state.error = null;
      })
      .addCase(generatePublicQuestion.fulfilled, (state, action) => {
        if (action.payload.project_type === 'adaptive_forms') {
          // create new object and place object right before end page
          // extract files from welcome page
          const welcomePage = state.formPages.find(
            (page) => page.page_type === 'welcome'
          );
          const newFormPages = [...state.formPages];
          const endPageIndex = newFormPages.findIndex(
            (page) => page.page_type === 'end'
          );
          // for adaptive forms we want to add the files from the welcome page to the new prompt page
          // this way we can be sure each generated adapative form page has the same files
          const newPage = {
            position: action.payload.correct_index,
            page_type: 'prompt',
            title: action.payload.question,
            statement: action.payload.statement,
            settings: null,
            form_page_files: welcomePage?.form_page_files || [],
          };
      
          newFormPages.splice(endPageIndex, 0, newPage);

          state.formPages = newFormPages;
        }
 
        state.generatingFormPageQuestionIsLoading = false;
        state.generatedQuestion = action.payload.question;

        state.answerType = action.payload.answer_type;
        state.answerTypeValue = action.payload.answer_type_value || null;
        state.options = action.payload.options;
        state.generatedQuestionOptions = action.payload.options;
        state.conversationUuid = action.payload.conversation_uuid;
        state.isRequired = action.payload.is_required;
        state.callToActionUrl = action.payload.call_to_action_url;
        state.callToActionTarget = action.payload.call_to_action_target;
        state.isConversationCompleted =
          action.payload.is_conversation_completed;
        state.correctIndex = action.payload.correct_index;
        state.clarityExplanation = action.payload.clarity_explanation || null;
        
        // Set respondentInsights if available in the response
        if (action.payload.respondent_insights && state.showOtherRespondents) {
          state.respondentInsights = action.payload.respondent_insights;
        }
        
        // Set userDetails if available in the response when conversation is completed
        if (action.payload.user_details) {
          state.userDetails = action.payload.user_details;
        }

        if (action.payload.is_conversation_completed) {
          removeConversationIdCookie(state.currentProject?.id);
        }
      })
      .addCase(generatePublicQuestion.rejected, (state, action) => {
        state.generatingFormPageQuestionIsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyFormPassword.pending, (state) => {
        state.verifyingPassword = true;
        state.error = null;
      })
      .addCase(verifyFormPassword.fulfilled, (state) => {
        state.verifyingPassword = false;
        state.isPasswordVerified = true;
      })
      .addCase(verifyFormPassword.rejected, (state, action) => {
        state.verifyingPassword = false;
        state.isPasswordVerified = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetPublicProjectState } = publicProjectSlice.actions;
export default publicProjectSlice.reducer;
